import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'

import ScreenLayout from '@/components/layout/ScreenLayout'
import CountUp from '@/components/ui/CountUp'
import DataTable, { type DataTableColumn } from '@/components/ui/DataTable'
import EmptyState from '@/components/ui/EmptyState'
import Money from '@/components/ui/Money'
import NotAvailable from '@/components/ui/NotAvailable'
import TermTip from '@/components/ui/TermTip'
import TitleRow from '@/components/ui/TitleRow'
import RefreshRegion from '@/components/ui/RefreshRegion'
import UpdatingNote from '@/components/ui/UpdatingNote'
import { useIndexSymbols, useVolatileStocks } from '@/features/signals/api/queries'
import { VOLATILE_SEARCH_AVAILABLE } from '@/features/signals/api/signalsFlags'
import { useOnline } from '@/hooks/useOnline'
import { formatMoney } from '@/lib/format'
import { money } from '@/lib/money'
import { lastUpdatedMeta, resolveViewState } from '@/lib/view-state'
import type { SwingRow } from '@/schemas/signals'
import MissingFigure from '@/screens/signals/components/MissingFigure'
import { signalCompanyColumn } from '@/screens/signals/components/SignalCompanyColumn'
import PastSessionEmpty from '@/screens/signals/components/PastSessionEmpty'
import SessionDatePicker from '@/screens/signals/components/SessionDatePicker'
import SignalsEmpty from '@/screens/signals/components/SignalsEmpty'
import SignalsLocked from '@/screens/signals/components/SignalsLocked'
import SignalsRouteError from '@/screens/signals/components/SignalsRouteError'
import SignalsSearchBox from '@/screens/signals/components/SignalsSearchBox'
import { PickerSkeleton } from '@/screens/signals/components/SignalsSkeletons'
import SignalsTableFrame from '@/screens/signals/components/SignalsTableFrame'
import {
  SearchSkeleton,
  TableFrameSkeleton,
  ToolbarSkeleton,
} from '@/screens/signals/components/SignalsTableSkeletons'
import SignalsToolbar from '@/screens/signals/components/SignalsToolbar'
import { signalsRailProps } from '@/screens/signals/components/signals-rail'
import { useSearchBox } from '@/screens/signals/lib/search-box'
import { useIsPastSession } from '@/screens/signals/lib/session-status'
import { SIGNAL_ROWS_52 } from '@/screens/signals/lib/signal-rows'
import { isSignalsLocked, signalRowLink } from '@/screens/signals/lib/signals-ui'
import {
  loadVolatileStocks,
  volatileStocksSearchSchema,
} from '@/screens/signals/VolatileStocksScreen.loader'
import { SIGNAL_DEFAULT_PAGE_SIZE, toSignalPageSize } from '@/screens/signals/search'

const TITLE = 'Volatile Stocks'

/** The strip names the page, so the header is the bare title (`sr-only` from `lg`). */
const HEADER = <TitleRow title={TITLE} />

/** The search box at the left and the static date at the right, in bars. */
const PENDING_TOOLBAR = <ToolbarSkeleton start={<SearchSkeleton />} end={<PickerSkeleton />} />

/*
 * The loaded page, block for block: the search and date row, the table panel on 52px rows
 * (fifteen, the opening page size) and its pager. The search is the mock's, held behind
 * `VOLATILE_SEARCH_AVAILABLE` until the endpoint takes `q`. No subtitle or footnote (owner
 * 2026-10-12).
 */
function VolatileStocksPending() {
  return (
    <ScreenLayout
      pending
      pendingLabel="Loading volatile stocks…"
      header={HEADER}
      {...signalsRailProps(TITLE)}
    >
      <TableFrameSkeleton
        rows={SIGNAL_DEFAULT_PAGE_SIZE}
        columns={3}
        toolbar={PENDING_TOOLBAR}
        tableClassName={SIGNAL_ROWS_52}
      />
    </ScreenLayout>
  )
}

function VolatileStocksError(props: ErrorComponentProps) {
  return (
    <SignalsRouteError
      {...props}
      header={HEADER}
      pageTitle={TITLE}
      title="Volatile stocks did not load"
    />
  )
}

export const Route = createFileRoute('/_app/market/signals/volatile')({
  validateSearch: volatileStocksSearchSchema,
  loader: loadVolatileStocks,
  pendingComponent: VolatileStocksPending,
  errorComponent: VolatileStocksError,
  component: VolatileStocksScreen,
})

/** One frame of the swing's count-up, at the API's 2 dp. */
const formatSwing = (value: number): string => `${formatMoney(money(value.toFixed(2)))}%`

/**
 * Swing to the API's 2 dp (the mock's 1 dp is its sample; the app shows the API's places), 14/400
 * in the primary ink and counting up from 0 on mount (A4, 700ms; reduced motion shows the final
 * figure). The API's order (swing, highest first) is kept, so no column sorts (D6). LTP is 14/400
 * secondary under a glossary head (S9). Figure columns 140 and 120 hug the right edge; Company takes
 * the rest.
 */
const COLUMNS: readonly DataTableColumn<SwingRow>[] = [
  signalCompanyColumn<SwingRow>({ fill: true }),
  {
    id: 'swing',
    accessorFn: (row) => row.swingPercent,
    header: 'Daily swing',
    enableSorting: false,
    meta: {
      numeric: true,
      width: '152px',
      sortHint: 'The average daily high-to-low move, as a share of the low',
    },
    cell: ({ row }) => <CountUp value={Number(row.original.swingPercent)} format={formatSwing} />,
  },
  {
    id: 'ltp',
    accessorFn: (row) => row.ltp ?? '',
    header: () => (
      <TermTip term="price" variant="band">
        LTP (Rs)
      </TermTip>
    ),
    enableSorting: false,
    meta: { numeric: true, secondary: true, width: '120px' },
    cell: ({ row }) =>
      row.original.ltp === null ? <MissingFigure /> : <Money value={row.original.ltp} />,
  },
]

/**
 * Signal Zone · Volatile stocks — the widest average daily swings over the
 * last 30 sessions, biggest first, fifteen to a page.
 *
 * MAIN read keyed on the page, so `useQuery` + `keepPreviousData`: turning the
 * page keeps the old rows on screen under RefreshRegion's refresh line, and
 * the table's row entrance does not replay. The date at the toolbar's right is the session
 * picker; the read takes no date, so a past pick shows `PastSessionEmpty`
 * (`asks/signals-dates.md`).
 */
function VolatileStocksScreen() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const online = useOnline()
  const [term, setTerm] = useSearchBox(search.q, (q) => {
    void navigate({ search: (prev) => ({ ...prev, q, page: 1 }), replace: true })
  })
  // The endpoint takes no `q` yet: a typed search leaves the box in place over "Not available yet"
  // and sends nothing (VOLATILE_SEARCH_AVAILABLE).
  const searching = search.q !== undefined
  const unavailable = searching && !VOLATILE_SEARCH_AVAILABLE
  const query = useVolatileStocks({
    page: search.page,
    pageSize: search.pageSize,
    q: VOLATILE_SEARCH_AVAILABLE ? (search.q ?? null) : null,
  })
  const view = resolveViewState(query, { online })
  const data = query.data
  const isUpdating = query.isPlaceholderData
  const indexSymbols = useIndexSymbols()
  // Every branch keeps the date row the loaded table draws, so a state does not drop it.
  const past = useIsPastSession(search.date)
  const picker = (
    <SessionDatePicker
      value={search.date ?? null}
      onChange={(date) => {
        void navigate({ search: (prev) => ({ ...prev, date, page: 1 }) })
      }}
    />
  )
  const searchBox = <SignalsSearchBox value={term} onChange={setTerm} />
  const toolbar = <SignalsToolbar start={searchBox} end={picker} />

  return (
    <ScreenLayout header={HEADER} {...signalsRailProps(TITLE)}>
      {past && search.date !== undefined ? (
        <div>
          {toolbar}
          <PastSessionEmpty date={search.date} />
        </div>
      ) : unavailable ? (
        <div>
          {toolbar}
          <NotAvailable thing="Searching volatile stocks" />
        </div>
      ) : view === 'failed' || view === 'offline' ? (
        isSignalsLocked(query.error) ? (
          <SignalsLocked error={query.error} />
        ) : (
          <div>
            {toolbar}
            <EmptyState
              kind={view}
              size="page"
              onRetry={() => void query.refetch()}
              retrying={query.isFetching}
              meta={view === 'failed' ? lastUpdatedMeta(query.dataUpdatedAt) : undefined}
              {...(view === 'failed' ? { title: 'Couldn’t load volatile stocks' } : {})}
            />
          </div>
        )
      ) : data === undefined ? (
        <TableFrameSkeleton
          rows={search.pageSize}
          columns={3}
          toolbar={PENDING_TOOLBAR}
          tableClassName={SIGNAL_ROWS_52}
        />
      ) : data.rows.length === 0 ? (
        <div>
          {toolbar}
          <RefreshRegion updating={isUpdating}>
            {data.total > 0 ? (
              <SignalsEmpty
                reason="past-end"
                of="the list"
                onFirstPage={() => {
                  void navigate({ search: (prev) => ({ ...prev, page: 1 }) })
                }}
              />
            ) : searching ? (
              <EmptyState kind="no-results" size="section" query={search.q ?? ''} />
            ) : (
              <SignalsEmpty
                reason="list"
                title="No volatile stocks yet"
                description="Swings are measured over the last 30 sessions, and nothing has been computed."
              />
            )}
          </RefreshRegion>
        </div>
      ) : (
        <>
          <SignalsTableFrame
            toolbar={searchBox}
            toolbarEnd={picker}
            paging={{
              page: data.page,
              pageSize: data.pageSize,
              total: data.total,
              unit: 'stocks',
              onPageChange: (page) => {
                void navigate({ search: (prev) => ({ ...prev, page }) })
              },
              onPageSizeChange: (pageSize) => {
                void navigate({
                  search: (prev) => ({
                    ...prev,
                    pageSize: toSignalPageSize(pageSize, search.pageSize),
                    page: 1,
                  }),
                })
              },
            }}
          >
            <RefreshRegion updating={isUpdating}>
              <DataTable
                columns={COLUMNS}
                rows={data.rows}
                getRowId={(row) => row.stock.symbol}
                rowLinkOptions={signalRowLink(indexSymbols)}
                rowHeight="lg"
                bodySize="m"
                headWeight="regular"
                washTone="wash"
                nameUnderline={false}
                className={SIGNAL_ROWS_52}
                caption="Stocks with the widest average daily swing over the last 30 sessions"
              />
            </RefreshRegion>
          </SignalsTableFrame>
          <UpdatingNote when={isUpdating} />
        </>
      )}
    </ScreenLayout>
  )
}
